import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useProjectId } from 'store/project';
import { useBacktrack } from 'components/common/withBacktrack';
import { useAppNavigation } from 'contexts/navigation';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import {
  isCreatePath,
  isEditPath,
  isInspectionChecklistPath,
} from 'setup/navigation/routeMatchers';
import {
  useRequiredInspectionTemplate,
} from 'components/dataProviders/withRequiredInspectionTemplate';
import {
  InspectionSubmitResponse,
  getInspectionStoreState,
  useInspection,
  useInspectionStore,
  useInspectionSubmit,
} from 'store/inspection';
import { SubmitOptions } from 'components/dataCreationForms/withInputForm';
import { DocumentsOnInspectionView } from 'presentation/document/documentsToDocumentsOnInspectionView';
import { inspectionToFlatForm, SUBMIT_EVENT_TYPE } from '../model';
import { InspectionWizardControllerResult } from '../types';
import { useInspectionDocuments } from '../hooks/useInspectionDocuments';
import useFormEdit from 'views/issue/forms/useFormEdit';

const EMPTY_INSPECTION_DOCUMENTS: DocumentsOnInspectionView = {};

export function useInspectionWizardController(): InspectionWizardControllerResult {
  const projectId = useProjectId();
  const location = useLocation();
  const hasRedirectedToCreateInspectionRef = useRef(false);
  const [hasRenderedForm, setHasRenderedForm] = useState(false);

  const { setEditing } = useFormEdit();
  const { back } = useBacktrack();
  const { goToCreateInspection, goToInspectionDetails } = useAppNavigation();
  const inspectionIdParam = useAppUrlParams(UrlParamName.INSPECTION_ID);
  const isCreateMode = !inspectionIdParam;

  const setSkipRedirectAfterSave =
    useInspectionStore.use.setSkipRedirectAfterSave();

  // Clears the flag a protocol item sets before leaving the wizard to open an issue.
  useEffect(() => {
    setSkipRedirectAfterSave(false);
  }, [setSkipRedirectAfterSave]);

  const { template, loading: loadingTemplate } =
    useRequiredInspectionTemplate();

  const {
    inspection: storeInspection,
    inspectionModel: storeInspectionModel,
    loading: loadingInspection,
    setIgnoreDocuments,
  } = useInspection();

  // In create mode, store data is stale (from previous inspection) - ignore it.
  // This prevents form from initializing with old values before store resets.
  const inspection = isCreateMode ? undefined : storeInspection;
  const inspectionModel = isCreateMode ? undefined : storeInspectionModel;

  const {
    inspectionDocuments: storeInspectionDocuments,
    documentsCount,
    documentsLoading,
  } = useInspectionDocuments(inspectionModel);

  const inspectionDocuments = isCreateMode
    ? EMPTY_INSPECTION_DOCUMENTS
    : storeInspectionDocuments;

  const onDone = useCallback(
    (response: InspectionSubmitResponse) => {
      const currentPathname =
        typeof window !== 'undefined'
          ? window.location.pathname
          : location.pathname;
      const isOnIssuePage =
        currentPathname.includes('/issue/') &&
        !currentPathname.includes('/inspection');
      const shouldSkipRedirect =
        getInspectionStoreState().skipRedirectAfterSave;

      if (shouldSkipRedirect || isOnIssuePage) {
        setSkipRedirectAfterSave(false);
        return Promise.resolve(response);
      }

      if (response) {
        goToInspectionDetails(projectId, response.inspection._id, {
          replace: true,
        });
      }

      return Promise.resolve(response);
    },
    [
      goToInspectionDetails,
      location.pathname,
      projectId,
      setSkipRedirectAfterSave,
    ]
  );

  const { submitForm, isSaving } = useInspectionSubmit(onDone);

  const isEditMode = !isCreateMode && Boolean(inspectionModel);


  const waitingForInitialDocuments =
    documentsLoading && documentsCount === 0;
  const shouldWaitForInspection = !isCreateMode && loadingInspection;

  const isInitialLoading =
    !hasRenderedForm &&
    ((loadingTemplate && !template) ||
      shouldWaitForInspection ||
      (isEditMode && waitingForInitialDocuments));

  const loading = isInitialLoading;

  useEffect(() => {
    if (!inspection && !loading) {
      setIgnoreDocuments(true);
    } else if (inspection) {
      setIgnoreDocuments(false);
    }
  }, [inspection, loading, setIgnoreDocuments]);

  useEffect(() => {
    const isAlreadyOnCreateRoute = isCreatePath(location.pathname);

    if (
      !inspection &&
      !loading &&
      template?._id &&
      !hasRedirectedToCreateInspectionRef.current &&
      !isEditPath(location.pathname) &&
      !isAlreadyOnCreateRoute
    ) {
      hasRedirectedToCreateInspectionRef.current = true;
      goToCreateInspection(template._id);
    }
  }, [
    goToCreateInspection,
    inspection,
    loading,
    location.pathname,
    template?._id,
  ]);

  useEffect(() => {
    const shouldSetEditing =
      (isEditPath(location.pathname) ||
        isInspectionChecklistPath(location.pathname)) &&
      inspection &&
      !loading;

    if (shouldSetEditing) {
      setEditing(true, 'inspection');
    }
  }, [inspection?._id, loading, location.pathname, setEditing]);

  const waitingForInspectionTransform = isEditMode && !inspection;
  const shouldShowSpinner =
    !hasRenderedForm &&
    (loading || !template || waitingForInspectionTransform);

  useEffect(() => {
    if (!shouldShowSpinner && !hasRenderedForm) {
      setHasRenderedForm(true);
    }
  }, [hasRenderedForm, shouldShowSpinner]);

  const initialValues = useMemo(() => {
    return inspectionToFlatForm(inspection, inspectionDocuments);
  }, [inspection, inspectionDocuments]);

  const dispatchSubmit = useCallback((options?: SubmitOptions) => {
    window.dispatchEvent(
      new CustomEvent(SUBMIT_EVENT_TYPE, {
        detail: { finish: options?.finish || false },
      })
    );
  }, []);

  return {
    loading: shouldShowSpinner,
    template,
    inspection,
    initialValues,
    submitForm,
    isSaving,
    back,
    dispatchSubmit,
  };
}
